import { parseISO } from 'date-fns';
import React, { useState } from 'react';

import { Checkbox, CheckboxGroup } from 'src/common/ui/components/Checkbox';
import { Link } from 'src/common/ui/components/Link';
import useTypedSWR from 'src/components/general/useTypedSWR';
import { requestTypeMap } from 'src/dropInClinic/RequestType';
import { DROP_IN_ATC_INTERFACE } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { ActionButtons } from 'src/popInClinic/ActionButtons';
import { PopInClinicSettings } from 'src/popInClinic/settings/PopInClinicSettings';
import { RequestType } from 'src/popInClinic/types';
import { GET_POP_IN_CLINIC_QUEUE } from 'src/popInClinic/usePopInClinicQueue.gql';
import { Provider } from 'src/shared/stores/resource';
import useRootStore from 'src/stores/useRootStore';
import { ProviderRole } from 'src/stores/users/userType';

const AUTO_REFRESH_MS = 15 * 1000;

const ALL_REQUEST_TYPES = (
  Object.keys(requestTypeMap) as Array<keyof typeof requestTypeMap>
).filter(
  t => t !== 'CASE_MANAGER_VISIT', // obsolete
);

// Each role lands on the queue pre-filtered to the request type it primarily works:
// MA (CareAdvocate) preps intakes, Peer (PeerCoach) runs peer visits, and
// Registered Nurse (RegisteredNurse) handles follow-ups and reengagements. Everyone else
// (Clinician, OtherStaff, unknown) sees the full queue.
function getDefaultEnabledTypes(role: ProviderRole | null): Set<string> {
  switch (role) {
    case ProviderRole.CareAdvocate:
      return new Set<string>([RequestType.Intake]);
    case ProviderRole.PeerCoach:
      return new Set<string>([RequestType.PeerVisit]);
    case ProviderRole.RegisteredNurse:
      return new Set<string>([RequestType.FollowUp, RequestType.Reengagement]);
    default:
      return new Set<string>(ALL_REQUEST_TYPES);
  }
}

export function PopInClinicQueueTab(): JSX.Element {
  const { data, error, mutate } = useTypedSWR(GET_POP_IN_CLINIC_QUEUE, {
    refreshInterval: AUTO_REFRESH_MS,
  });
  const showSettings = useFeatureFlag(DROP_IN_ATC_INTERFACE);

  const {
    auth: { user: currentUser },
  } = useRootStore();
  const providerRole = ((currentUser as Provider)?.teamRole as ProviderRole | null) ?? null;

  const showRequestWeight = providerRole === ProviderRole.OtherStaff;

  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(() =>
    getDefaultEnabledTypes(providerRole),
  );
  const [, setRequestUpdateError] = useState<unknown | null>(null);

  const toggleType = (type: string) => {
    setEnabledTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  if (error) {
    return <div>Error loading queue: {String(error)}</div>;
  }

  if (!data) {
    return <div>Loading queue…</div>;
  }

  const requests = data.staff_getPopInClinicQueue.filter(r => enabledTypes.has(r.requestType));

  // Column-def array (not a flat string list) so the optional Request Weight column can be
  // dropped without re-indexing the centering rule — index-based centering silently breaks
  // alignment of later columns once a column is removed.
  const columns: Array<{ label: string; center: boolean }> = [
    { label: 'Patient', center: false },
    { label: 'State', center: false },
    { label: 'Type', center: false },
    { label: 'Time Waited', center: false },
    ...(showRequestWeight ? [{ label: 'Request Weight', center: true }] : []),
    { label: 'Status', center: false },
    { label: 'Straight­forward', center: true },
    { label: '', center: false },
  ];

  return (
    <>
      <CheckboxGroup direction="row">
        {ALL_REQUEST_TYPES.map(type => (
          <Checkbox
            key={type}
            label={requestTypeMap[type]}
            checked={enabledTypes.has(type)}
            onChange={() => toggleType(type)}
          />
        ))}
      </CheckboxGroup>
      {requests.length === 0 ? (
        <p>Queue is empty</p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.label || `col-${i}`}
                  style={{
                    textAlign: col.center ? 'center' : 'left',
                    padding: '0.5rem',
                    borderBottom: '1px solid #ccc',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.map(r => (
              <tr key={r.id}>
                <td style={{ padding: '0.5rem' }}>
                  <Link href={`/patients/${r.patientId}`} external>
                    {r.patientName || r.patientId}
                  </Link>
                </td>
                <td style={{ padding: '0.5rem' }}>{r.patientState ?? '—'}</td>
                <td style={{ padding: '0.5rem' }}>
                  {requestTypeMap[r.requestType as keyof typeof requestTypeMap] ?? r.requestType}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {(() => {
                    const elapsedMs = Date.now() - parseISO(r.requestTime as string).getTime();
                    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
                    const waitMinutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
                    return `${waitMinutes} minutes`;
                  })()}
                </td>
                {showRequestWeight && (
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>{r.score}</td>
                )}
                <td style={{ padding: '0.5rem' }}>{formatSnakeCase(r.status)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                  {r.isStraightforward ? (
                    <span aria-label="Straightforward">⚡</span>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <ActionButtons
                      entry={{
                        requestId: r.id,
                        requestType: r.requestType,
                        status: r.status,
                        acceptable: true,
                        insuranceIsAcceptable: undefined,
                        stateIsAcceptable: undefined,
                        categories: [],
                        queuedAt: parseISO(r.requestTime as string),
                        preparedBy: null,
                        legacyReason: null,
                        reason: null,
                        hasBeenSufficientlyNotified: false,
                        patient: {
                          id: r.patientId,
                          firstName: r.patientFirstName,
                          lastName: r.patientLastName,
                          preferredName: r.patientPreferredName ?? undefined,
                          homeState: r.patientState ?? '',
                        },
                      }}
                      providerRole={providerRole}
                      setRequestUpdateError={setRequestUpdateError}
                      mutate={() => {
                        mutate();
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showSettings && <PopInClinicSettings />}
    </>
  );
}

function formatSnakeCase(value: string) {
  return value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
